import { useState, useEffect } from "react";
import { X, Cookie } from "lucide-react";

const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    performance: false,
    marketing: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem("cookieConsent");
    if (!consent) {
      setShowBanner(true);
    }
  }, []);

  const acceptAll = () => {
    const allConsent = { necessary: true, performance: true, marketing: true };
    localStorage.setItem("cookieConsent", JSON.stringify(allConsent));
    setShowBanner(false);
    
    // Google Consent Mode v2
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted',
        'ad_storage': 'granted',
        'ad_user_data': 'granted',
        'ad_personalization': 'granted'
      });
    }
  };

  const rejectAll = () => {
    const minimalConsent = { necessary: true, performance: false, marketing: false };
    localStorage.setItem("cookieConsent", JSON.stringify(minimalConsent));
    setShowBanner(false);
    
    // Google Consent Mode v2
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'denied',
        'ad_storage': 'denied',
        'ad_user_data': 'denied',
        'ad_personalization': 'denied'
      });
    }
  };

  const savePreferences = () => {
    localStorage.setItem("cookieConsent", JSON.stringify(preferences));
    setShowBanner(false);
    setShowPreferences(false);
    
    // Google Consent Mode v2
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': preferences.performance ? 'granted' : 'denied',
        'ad_storage': preferences.marketing ? 'granted' : 'denied',
        'ad_user_data': preferences.marketing ? 'granted' : 'denied',
        'ad_personalization': preferences.marketing ? 'granted' : 'denied'
      });
    }
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t-2 border-[#059669] shadow-2xl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {!showPreferences ? (
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-start gap-3 flex-1">
              <Cookie className="w-6 h-6 text-[#059669] flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Çerez Kullanımı</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Web sitemizde deneyiminizi geliştirmek, site trafiğini analiz etmek ve pazarlama faaliyetlerini yürütmek amacıyla çerezler kullanmaktayız.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-3 w-full md:w-auto">
              <button
                onClick={rejectAll}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                Reddet
              </button>
              <button
                onClick={() => setShowPreferences(true)}
                className="px-4 py-2 border border-[#059669] text-[#059669] rounded-lg text-sm font-medium hover:bg-[#059669] hover:text-white transition-colors"
              >
                Tercihleri Yönet
              </button>
              <button
                onClick={acceptAll}
                className="px-4 py-2 bg-[#059669] text-white rounded-lg text-sm font-medium hover:bg-[#047857] transition-colors"
              >
                Kabul Et
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Çerez Tercihleri</h3>
              <button
                onClick={() => setShowPreferences(false)}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            <div className="space-y-3">
              {/* Zorunlu Çerezler */}
              <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 mb-1">Zorunlu Çerezler</h4>
                  <p className="text-sm text-gray-600">Sitenin temel işlevselliği için gereklidir. Devre dışı bırakılamaz.</p>
                </div>
                <div className="w-12 h-6 bg-[#059669] rounded-full flex items-center justify-end px-1 ml-4">
                  <div className="w-4 h-4 bg-white rounded-full"></div>
                </div>
              </div>

              {/* Performans Çerezleri */}
              <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 mb-1">Performans Çerezleri</h4>
                  <p className="text-sm text-gray-600">Site kullanım istatistiklerini toplamamıza yardımcı olur.</p>
                </div>
                <button
                  onClick={() => setPreferences(prev => ({ ...prev, performance: !prev.performance }))}
                  className={`w-12 h-6 rounded-full flex items-center ${preferences.performance ? 'justify-end bg-[#059669]' : 'justify-start bg-gray-300'} px-1 ml-4 transition-all`}
                >
                  <div className="w-4 h-4 bg-white rounded-full"></div>
                </button>
              </div>

              {/* Pazarlama Çerezleri */}
              <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 mb-1">Pazarlama Çerezleri</h4>
                  <p className="text-sm text-gray-600">İlgi alanlarınıza göre reklam sunmamızı sağlar.</p>
                </div>
                <button
                  onClick={() => setPreferences(prev => ({ ...prev, marketing: !prev.marketing }))}
                  className={`w-12 h-6 rounded-full flex items-center ${preferences.marketing ? 'justify-end bg-[#059669]' : 'justify-start bg-gray-300'} px-1 ml-4 transition-all`}
                >
                  <div className="w-4 h-4 bg-white rounded-full"></div>
                </button>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowPreferences(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                İptal
              </button>
              <button
                onClick={savePreferences}
                className="px-4 py-2 bg-[#059669] text-white rounded-lg text-sm font-medium hover:bg-[#047857] transition-colors"
              >
                Tercihleri Kaydet
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieBanner;
